/**
 * AudioPlayerService - Singleton for TTS audio playback
 * 
 * Manages a single HTMLAudioElement instance.
 * Handles URL resolution, autoplay unlock, and state management.
 * 
 * NOTE: Audio event listeners (playing/pause/ended/error) are managed by
 * useAudio hook, NOT here. This avoids duplicate state updates.
 */

import { config } from '@/config'
import { useAudioStore } from '@/stores/audioStore'

class AudioPlayerService {
  private _audio: HTMLAudioElement
  private _isStopping: boolean = false
  private _isSkipping: boolean = false
  private _unlocked: boolean = false
  private _playbackFinishedEmitted: boolean = false

  get playbackFinishedEmitted(): boolean {
    return this._playbackFinishedEmitted
  }

  markPlaybackFinished(): void {
    this._playbackFinishedEmitted = true
  }

  resetPlaybackFinished(): void {
    this._playbackFinishedEmitted = false
  }

  constructor() {
    this._audio = new Audio()
    this._audio.preload = 'auto'
  }

  play(url: string): void {
    this.stop()
    this.resetPlaybackFinished()
    const resolvedUrl = this._resolveUrl(url)
    console.log('[AudioService] Playing URL:', resolvedUrl)
    this._audio.src = resolvedUrl
    this._audio.load()
    this._audio.play().catch((err) => {
      console.error('[AudioService] play() failed:', err.message)
      if (err.name === 'NotAllowedError') {
        console.warn('[AudioService] Autoplay blocked - user gesture required')
      }
      useAudioStore.getState().setError()
    })
  }

  pause(): void {
    this._audio.pause()
  }

  resume(): void {
    this._audio.play().catch((err) => {
      console.error('[AudioService] resume() failed:', err.message)
    })
  }

  stop(): void {
    this._isStopping = true
    this.resetPlaybackFinished()
    this._audio.pause()
    this._audio.currentTime = 0
    this._audio.removeAttribute('src')

    queueMicrotask(() => {
      this._isStopping = false
    })
  }

  mute(): void {
    this._audio.muted = true
  }

  unmute(): void {
    this._audio.muted = false
  }

  skip(): void {
    this._isSkipping = true
    this._audio.pause()

    queueMicrotask(() => {
      this._isSkipping = false
    })
  }

  replay(): void {
    this._audio.currentTime = 0
    this._audio.play().catch((err) => {
      console.error('[AudioService] replay() failed:', err.message)
    })
  }

  getElement(): HTMLAudioElement {
    return this._audio
  }

  isStopping(): boolean {
    return this._isStopping
  }

  isSkipping(): boolean {
    return this._isSkipping
  }

  /**
   * Unlock autoplay by playing a silent audio clip.
   * Must be called from a user gesture (click/touch) handler.
   * Does NOT interrupt currently playing audio.
   */
  unlock(): void {
    if (this._unlocked) return

    // If audio is currently playing, just mark as unlocked
    if (!this._audio.paused && this._audio.src) {
      this._unlocked = true
      console.log('[AudioService] Autoplay unlocked (already playing)')
      return
    }

    const silentWav = 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAAB9AAACABAAZGF0YQIAAAAAAA=='
    this._audio.src = silentWav
    this._audio.play()
      .then(() => {
        this._audio.pause()
        this._audio.removeAttribute('src')
        this._unlocked = true
        console.log('[AudioService] Autoplay unlocked')
      })
      .catch((err) => {
        console.debug('[AudioService] Unlock attempt failed:', err.message)
      })
  }

  private _resolveUrl(url: string): string {
    if (!url.startsWith('/')) {
      return url
    }
    const origin = new URL(config.apiUrl).origin
    return origin + url
  }
}

export const audioService = new AudioPlayerService()
